import React, { useEffect, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import * as turf from '@turf/turf';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import Sidebar1 from '../components/Sidebar2';

import { FaDatabase, FaProjectDiagram } from 'react-icons/fa';
import { BsPeopleFill, BsFolder2Open } from 'react-icons/bs';

import {
  MdArticle,
  MdBuild,
  MdLink,
  MdSpaceDashboard,
} from 'react-icons/md';

import { TbScale } from 'react-icons/tb';
import ReactDOMServer from 'react-dom/server';


/* ============================================================
   LOCATION ICON
============================================================ */

const locationIcon = new L.Icon({
  iconUrl:
    'https://cdn-icons-png.flaticon.com/512/2776/2776067.png',

  iconSize: [36, 36],
  iconAnchor: [18, 36],
  popupAnchor: [0, -36],
});


/* ============================================================
   IMAGE ICONS
============================================================ */

const imageIcons = {
  products:
    'https://images.icon-icons.com/2664/PNG/96/water_drops_icon_161227.png',
};


/* ============================================================
   FIELD LABELS
============================================================ */

/*
 IMPORTANT:

 getFieldLabel()
 ----------------
 This is used internally to read the Excel columns.

 Therefore tools MUST remain "Tools".

 Do not change this to Dashboard because the Excel sheet
 contains the column "Tools".
*/

const getFieldLabel = (key) =>
  ({
    primary: 'Primary Data Sets',

    products: 'Water Data Products',

    multiscale: 'Multi-Scale',

    publications: 'Publications',

    tools: 'Tools',

    stakeholders: 'Stakeholders',

    projects: 'Projects Using DIWASA Products',

    links: 'Links',

    usecase: 'UseCase',

    others: 'Tools (other)',
  }[key] || key);


/*
 DISPLAY LABELS
 --------------

 These labels are shown to the user.

 Therefore:

 Tools -> Dashboard
*/

const getDisplayLabel = (key) =>
  ({
    primary: 'Primary Data Sets',

    products: 'Water Data Products',

    multiscale: 'Multi-Scale',

    publications: 'Publications',

    tools: 'Dashboard',

    stakeholders: 'Stakeholders',

    projects: 'Projects Using DIWASA Products',

    links: 'Links',

    usecase: 'UseCase',

    others: 'Tools (other)',
  }[key] || key);


/* ============================================================
   ICONS
============================================================ */

const getIcon = (key) =>
  ({
    primary: (
      <FaDatabase
        size={28}
        color="#1976d2"
      />
    ),

    products: (
      <img
        src={imageIcons.products}
        alt="Water"
        width="28"
        height="28"
      />
    ),

    multiscale: (
      <TbScale
        size={28}
        color="#e38200"
      />
    ),

    publications: (
      <MdArticle
        size={28}
        color="#1a73e8"
      />
    ),

    /*
      UPDATED DASHBOARD ICON
    */

    tools: (
      <MdSpaceDashboard
        size={28}
        color="#6a1b9a"
      />
    ),

    stakeholders: (
      <BsPeopleFill
        size={28}
        color="#e53935"
      />
    ),

    projects: (
      <BsFolder2Open
        size={28}
        color="#8A2BE2"
      />
    ),

    links: (
      <MdLink
        size={28}
        color="#009688"
      />
    ),

    usecase: (
      <FaProjectDiagram
        size={28}
        color="#cc9900"
      />
    ),

    /*
      Tools (other) still uses build icon
    */

    others: (
      <MdBuild
        size={28}
        color="#666666"
      />
    ),
  }[key]);


/* ============================================================
   BASIC HELPERS
============================================================ */

const isUrl = (s = '') =>
  /^https?:\/\//i.test(String(s).trim());


const isYouTube = (u = '') =>
  /youtube\.com\/watch\?v=|youtu\.be\//i.test(u);


const isVimeo = (u = '') =>
  /vimeo\.com\/\d+/i.test(u);


const isMP4 = (u = '') =>
  /\.mp4(\?.*)?$/i.test(u);


const norm = (s = '') =>
  String(s)
    .trim()
    .toLowerCase();


const normalizeKey = (s = '') =>
  String(s)
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');


/* ============================================================
   SPLIT LIST CELLS
============================================================ */

const parseCell = (value = '') =>
  String(value)

    .split(/\r?\n|[;,]|•/g)

    .map((raw) => raw.trim())

    .filter(Boolean)

    .map((piece) => {

      const parts = piece
        .split('|')
        .map((p) => p.trim());


      if (parts.length === 1) {

        const only = parts[0];

        return isUrl(only)
          ? {
              title: '',
              url: only,
              description: '',
            }
          : {
              title: only,
              url: '',
              description: '',
            };
      }


      if (parts.length === 2) {

        return {
          title: parts[0],
          url: parts[1],
          description: '',
        };
      }


      return {
        title: parts[0],
        url: parts[1],
        description: parts
          .slice(2)
          .join(' | '),
      };
    });


/* ============================================================
   VIDEO COMPONENT
============================================================ */

const Video = ({ url, title }) => {

  if (isYouTube(url)) {

    const id =
      url.match(
        /(?:v=|youtu\.be\/)([A-Za-z0-9_-]{6,})/
      )?.[1];


    const embed = id
      ? `https://www.youtube.com/embed/${id}`
      : url;


    return (
      <iframe
        title={title || 'Use case video'}
        src={embed}
        style={{
          width: '100%',
          height: 360,
          border: 0,
          borderRadius: 10,
        }}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    );
  }


  if (isVimeo(url)) {

    const id =
      url.match(
        /vimeo\.com\/(\d+)/
      )?.[1];


    const embed = id
      ? `https://player.vimeo.com/video/${id}`
      : url;


    return (
      <iframe
        title={title || 'Use case video'}
        src={embed}
        style={{
          width: '100%',
          height: 360,
          border: 0,
          borderRadius: 10,
        }}
        allow="autoplay; fullscreen; picture-in-picture"
        allowFullScreen
      />
    );
  }


  if (isMP4(url)) {

    return (
      <video
        style={{
          width: '100%',
          borderRadius: 10,
        }}
        controls
      >
        <source
          src={url}
          type="video/mp4"
        />
      </video>
    );
  }


  return null;
};


/* ============================================================
   COLUMN ALIASES
============================================================ */

const COLUMN_ALIASES = {

  'Primary Data Sets': [
    'Primary Data Sets',
    'Primary Datasets',
    'Primary Data Set',
  ],

  'Water Data Products': [
    'Water Data Products',
    'Products',
    'Water Products',
  ],

  'Multi-Scale': [
    'Multi-Scale',
    'Multiscale',
    'Multi Scale',
  ],

  Publications: [
    'Publications',
    'Publication',
  ],

  Tools: [
    'Tools',
    'Tools (Dashboards)',
    'Dashboards',
  ],

  'Tools (other)': [
    'Tools (other)',
    'Tools (Other)',
    'Other Tools',
    'Others',
  ],

  Stakeholders: [
    'Stakeholders',
    'Stakeholder',
  ],

  projects: [
    'Projects Using DIWASA Products',
    'Projects using DIWASA Products',
    'Project Using DIWASA Products',
    'Project using DIWASA Products',
    'Projects',
    'Project',
  ],

  Links: [
    'Links',
    'Link',
    'Portfolio',
    'Portfolio Links',
  ],

  UseCase: [
    'UseCase',
    'Use Case',
    'Use Cases',
    'Use cases',
    'Use-Case',
  ],

  Country: [
    'Country',
  ],

  'River basin': [
    'River basin',
    'River Basin',
    'Basin',
  ],
};


/* ============================================================
   GET CELL
============================================================ */

const getCell = (row, logicalName) => {

  const tryNames =
    COLUMN_ALIASES[logicalName] ||
    [logicalName];


  const keys =
    Object.keys(row || {});


  for (const want of tryNames) {

    const wantNorm =
      String(want)
        .trim()
        .toLowerCase();


    const hit =
      keys.find(
        (k) =>
          String(k)
            .trim()
            .toLowerCase() === wantNorm
      );


    if (hit) {
      return row[hit];
    }
  }


  for (const want of tryNames) {

    const wantNorm =
      normalizeKey(want);


    const hit =
      keys.find(
        (k) =>
          normalizeKey(k) === wantNorm
      );


    if (hit) {
      return row[hit];
    }
  }


  return row?.[logicalName];
};


/* ============================================================
   SHEET HELPERS
============================================================ */

const normalizeTab = (s = '') =>
  s
    .replace(/[\s_-]/g, '')
    .toLowerCase();


const getSheetByAny = (
  wb,
  candidates = []
) => {

  const map =
    Object.fromEntries(
      (wb.SheetNames || [])
        .map((n) => [
          normalizeTab(n),
          n,
        ])
    );


  for (const c of candidates) {

    const key =
      normalizeTab(c);


    if (map[key]) {
      return wb.Sheets[map[key]];
    }
  }


  const hitKey =
    Object.keys(map)
      .find(
        (k) =>
          /usecases?/.test(k)
      );


  return hitKey
    ? wb.Sheets[map[hitKey]]
    : undefined;
};


/* ============================================================
   EMPTY VALUE CHECK
============================================================ */

const emptyish = (v) => {

  const t =
    String(v ?? '')
      .trim();


  return (
    t === '' ||
    /^(-|—|n\/a|na|null|none)$/i.test(t)
  )
    ? ''
    : t;
};


/* ============================================================
   STAKEHOLDER PARSING
============================================================ */

const cleanBullet = (line = '') =>
  line
    .replace(
      /^[\s•*·\-–—]+/,
      ''
    )
    .trim();


const looksLikeNewOrg = (line = '') => {

  if (!line) {
    return false;
  }


  const hasColonEarly =
    line.includes(':') &&
    line.indexOf(':') < 140;


  const hasUrl =
    /https?:\/\/[^\s)]+/i.test(line);


  const orgWord =
    /(authority|ministry|department|agency|institute|commission|service|board|office|secretariat|university|center|centre)/i;


  const titleLike =
    /^[A-Z][A-Za-z0-9&().,'’\- \t]{4,}$/.test(
      line
    );


  return (
    hasUrl ||
    hasColonEarly ||
    (
      orgWord.test(line) &&
      titleLike
    )
  );
};


const isFragment = (line = '') =>
  line.length <= 80 &&
  !/[.?!:]$/.test(line) &&
  !/https?:\/\/[^\s)]+/i.test(line);


/* ============================================================
   ENUMERATED TEXT
============================================================ */

const splitEnumerated = (text = '') => {

  const s =
    String(text || '')
      .replace(
        /\s*;\s*/g,
        '; '
      )
      .trim();


  const parts =
    s
      .split(
        /(?=(?:^|\s)(?:\(?\d{1,2}\)|\d{1,2}[.)])\s)/g
      )
      .map((t) => t.trim())
      .filter(Boolean);


  if (parts.length > 1) {

    return parts.map(
      (p) =>
        p.replace(
          /\s+/g,
          ' '
        )
    );
  }


  const bySemi =
    s
      .split(/[;\n]+/)
      .map((t) => t.trim())
      .filter(Boolean);


  if (bySemi.length > 1) {

    return bySemi.map(
      (t, i) =>
        `${i + 1}) ${t.replace(
          /^\(?\d{1,2}\)?[.)]\s*/,
          ''
        )}`
    );
  }


  return [s];
};


/* ============================================================
   PREAMBLE + LIST
============================================================ */

const peelPreambleAndList = (text = '') => {

  const s =
    String(text || '')
      .replace(/\r/g, '')
      .trim();


  if (!s) {

    return {
      preamble: '',
      list: [],
    };
  }


  const idx =
    s.search(
      /(?:^|\n|\s)(?:\(?1\)|1[.)])\s/
    );


  if (idx === -1) {

    return {
      preamble: s,
      list: [],
    };
  }


  const pre =
    s
      .slice(0, idx)
      .replace(
        /[;:\s]*$/g,
        ''
      )
      .trim();


  const rest =
    s.slice(idx);


  const list =
    splitEnumerated(rest)

      .map(
        (ln) =>
          ln
            .replace(
              /^\(?\d{1,2}\)?[.)]\s*/,
              ''
            )
            .trim()
      )

      .filter(Boolean);


  return {
    preamble: pre,
    list,
  };
};


/* ============================================================
   PARSE STAKEHOLDERS
============================================================ */

const parseStakeholders = (raw = '') => {

  const allLines =
    String(raw || '')
      .split(/\r?\n/)
      .map(cleanBullet)
      .filter(Boolean);


  const blocks = [];

  let cur = [];


  for (const ln of allLines) {

    if (
      looksLikeNewOrg(ln) &&
      cur.length
    ) {

      blocks.push(
        cur.join('\n')
      );

      cur = [ln];

    } else {

      cur.push(ln);
    }
  }


  if (cur.length) {

    blocks.push(
      cur.join('\n')
    );
  }


  return blocks.map((blk) => {

    let name = '';

    let body = blk;


    const idx =
      blk.indexOf(':');


    if (
      idx > -1 &&
      idx < 140
    ) {

      name =
        blk
          .slice(0, idx)
          .trim();


      body =
        blk
          .slice(idx + 1)
          .trim();

    } else {

      const first =
        blk
          .split(/\r?\n/)[0];


      if (looksLikeNewOrg(first)) {

        name = first;

        body =
          blk
            .slice(first.length)
            .trim();
      }
    }


    const urlMatch =
      body.match(
        /https?:\/\/[^\s)]+/i
      );


    const url =
      urlMatch
        ? urlMatch[0]
        : '';


    if (url) {

      body =
        body
          .replace(url, '')
          .trim();
    }


    const parts =
      body
        .split(/\r?\n/)
        .map(cleanBullet)
        .filter(Boolean);


    const merged = [];


    for (const ln of parts) {

      if (
        merged.length &&
        isFragment(ln)
      ) {

        merged[
          merged.length - 1
        ] +=
          (
            /,|;|:|\b(and|or)\b$/i.test(
              merged[
                merged.length - 1
              ]
            )
              ? ' '
              : ', '
          ) +
          ln.replace(
            /^and\s+/i,
            ''
          );

      } else {

        merged.push(ln);
      }
    }


    const paragraph =
      merged
        .join(' ')
        .replace(
          /\s{2,}/g,
          ' '
        )
        .trim();


    const lines =
      splitEnumerated(paragraph);


    return {
      name:
        name ||
        'Stakeholder',

      url,

      lines,

      body: paragraph,
    };
  });
};


/* ============================================================
   DYNAMIC USE CASES
============================================================ */

const makeHeaderScorer = () => {

  const tokens = {

    title:
      /(title|name|use\s*case|scheme|project)\b/i,

    desc:
      /(description|objective|aim|summary|overview|purpose)\b/i,

    problem:
      /(problem|challenge|issue|gap)\b/i,

    solutions:
      /(solution|approach|intervention|method)\b/i,

    delivery:
      /(delivery|mechanism|channel|tool)\b/i,

    owner:
      /(owner|lead|contact|focal|manager)\b/i,

    beneficiaries:
      /(benefici|user|end\s*user|target)\b/i,

    remark:
      /(remark|note|comment)\b/i,

    url:
      /(url|link|website|http)/i,

    activities:
      /(activit|task|work\s*stream|workstream)\b/i,

    country:
      /\bcountry\b/i,

    basin:
      /(basin|river|catchment)\b/i,

    region:
      /\b(region|location|area)\b/i,
  };


  const score = (
    header,
    which
  ) => {

    const h =
      String(header || '');


    const rx =
      tokens[which];


    return (
      rx &&
      rx.test(h)
    )
      ? (
          h.length <= 28
            ? 3
            : 2
        )
      : 0;
  };


  return {
    score,
    tokens,
  };
};


/* ============================================================
   INFER USE CASE SCHEMA
============================================================ */

const inferUseCaseSchema = (
  headers = []
) => {

  const {
    score,
    tokens,
  } =
    makeHeaderScorer();


  const best = (role) => {

    let bestH = '';

    let bestS = -1;


    for (const h of headers) {

      const s =
        score(
          h,
          role
        );


      if (s > bestS) {

        bestH = h;

        bestS = s;
      }
    }


    return (
      bestS > 0
    )
      ? bestH
      : '';
  };


  const titleKey =
    best('title') ||
    headers.find(
      (h) =>
        /name/i.test(h)
    ) ||
    headers[0] ||
    '';


  const descKey =
    best('desc');


  const problemKey =
    best('problem');


  const solutionsKey =
    best('solutions');


  const deliveryKey =
    best('delivery');


  const ownerKey =
    best('owner');


  const beneficiariesKey =
    best('beneficiaries');


  const remarkKey =
    best('remark');


  const urlKey =
    best('url');


  const activitiesKey =
    best('activities');


  const regionKeys =
    headers.filter(
      (h) =>
        tokens.country.test(h) ||
        tokens.basin.test(h) ||
        tokens.region.test(h)
    );


  return {

    titleKey,

    descKey,

    problemKey,

    solutionsKey,

    deliveryKey,

    ownerKey,

    beneficiariesKey,

    remarkKey,

    urlKey,

    activitiesKey,

    regionKeys,
  };
};


/* ============================================================
   TITLE NORMALIZATION
============================================================ */

const normTitle = (s = '') =>
  String(s)

    .toLowerCase()

    .replace(
      /[^a-z0-9\s]/g,
      ' '
    )

    .replace(
      /\s+/g,
      ' '
    )

    .trim();


/* ============================================================
   TITLE SCORE
============================================================ */

const tokenScore = (
  a,
  b
) => {

  const A =
    new Set(
      normTitle(a)
        .split(' ')
        .filter(Boolean)
    );


  const B =
    new Set(
      normTitle(b)
        .split(' ')
        .filter(Boolean)
    );


  if (
    !A.size ||
    !B.size
  ) {

    return 0;
  }


  let inter = 0;


  for (const t of A) {

    if (B.has(t)) {

      inter++;
    }
  }


  return (
    inter /
    Math.max(
      A.size,
      B.size
    )
  );
};


/* ============================================================
   FUZZY TITLE FIND
============================================================ */

const fuzzyFindTitleKey = (
  indexObj,
  title
) => {

  const keys =
    Object.keys(
      indexObj || {}
    );


  let best = null;

  let bestScore = 0;


  const t =
    normTitle(title);


  for (const k of keys) {

    const score =
      tokenScore(
        t,
        k
      ) +
      (
        k.includes(t) ||
        t.includes(k)
          ? 0.25
          : 0
      );


    if (score > bestScore) {

      bestScore = score;

      best = k;
    }
  }


  return (
    bestScore >= 0.55
  )
    ? best
    : null;
};


/* ============================================================
   BUILD USE CASE INDEX
============================================================ */

const buildUseCaseSheetIndexDynamic = (
  rows = []
) => {

  if (!rows.length) {

    return {
      sheetByTitle: {},
      byUrl: {},
    };
  }


  const headers =
    Object.keys(
      rows[0] || {}
    );


  const schema =
    inferUseCaseSchema(headers);


  const read = (
    row,
    k
  ) =>
    emptyish(
      row[
        schema[k]
      ]
    );


  const cleanActivity = (s) => {

    const t =
      emptyish(s);


    if (!t) {

      return '';
    }


    return t
      .replace(
        /^[;:•-]\s*/,
        ''
      )
      .trim();
  };


  const sheetByTitle = {};

  const byUrl = {};


  rows.forEach((r) => {

    let title =
      read(
        r,
        'titleKey'
      );


    if (!title) {

      const firstText =
        headers

          .map(
            (h) =>
              r[h]
          )

          .find(
            (v) =>
              typeof v === 'string' &&
              !isUrl(v) &&
              v.trim().length <= 120
          );


      title =
        emptyish(
          firstText
        );
    }


    if (!title) {

      return;
    }


    let regionVal = '';


    for (
      const rk
      of schema.regionKeys
    ) {

      const v =
        emptyish(
          r[rk]
        );


      if (v) {

        regionVal = v;

        break;
      }
    }


    const rawActs =
      r[
        schema.activitiesKey
      ];


    const acts =
      Array.isArray(rawActs)
        ? rawActs
            .map(cleanActivity)
            .filter(Boolean)

        : String(
            rawActs || ''
          )

            .split(
              /\r?\n|[;•]/
            )

            .map(cleanActivity)

            .filter(Boolean);


    const rec = {

      title,

      region: regionVal,

      description:
        read(
          r,
          'descKey'
        ),

      problem:
        read(
          r,
          'problemKey'
        ),

      solutions:
        read(
          r,
          'solutionsKey'
        ),

      delivery:
        read(
          r,
          'deliveryKey'
        ),

      owner:
        read(
          r,
          'ownerKey'
        ),

      beneficiaries:
        read(
          r,
          'beneficiariesKey'
        ),

      remark:
        read(
          r,
          'remarkKey'
        ),

      url:
        read(
          r,
          'urlKey'
        ),

      activities: acts,
    };


    const key =
      title.toLowerCase();


    if (
      !sheetByTitle[key]
    ) {

      sheetByTitle[key] = [];
    }


    sheetByTitle[key]
      .push(rec);


    if (rec.url) {

      const ukey =
        rec.url;


      if (
        !byUrl[ukey]
      ) {

        byUrl[ukey] = rec;
      }
    }
  });


  return {
    sheetByTitle,
    byUrl,
  };
};


/* ============================================================
   PICK USE CASE
============================================================ */

const pickUseCaseFromSheet = (
  sheetByTitle,
  title,
  region
) => {

  const tKey =
    norm(title);


  const r =
    norm(
      region || ''
    );


  let arr =
    sheetByTitle[tKey] ||
    [];


  if (!arr.length) {

    const bestKey =
      fuzzyFindTitleKey(
        sheetByTitle,
        title
      );


    if (bestKey) {

      arr =
        sheetByTitle[
          bestKey
        ] ||
        [];
    }
  }


  if (!arr.length) {

    return {};
  }


  const exact =
    arr.find(
      (e) =>
        e.region &&
        norm(e.region) === r
    );


  if (exact) {

    return exact;
  }


  const loose =
    arr.find(
      (e) =>
        e.region &&
        r &&
        norm(e.region)
          .includes(r)
    );


  if (loose) {

    return loose;
  }


  return (
    arr[0] ||
    {}
  );
};


/* ============================================================
   MAIN COMPONENT
============================================================ */

const MapView = () => {

  const mapRef =
    useRef(null);


  const geoJsonRef =
    useRef(null);


  const markerGroupRef =
    useRef(null);


  const legendRef =
    useRef(null);


  /* ========================================================
     STATES
  ======================================================== */

  const [
    category,
    setCategory,
  ] =
    useState('Country');


  const [
    selectedCountry,
    setSelectedCountry,
  ] =
    useState('All');


  const [
    expandedRegion,
    setExpandedRegion,
  ] =
    useState(null);


  const [
    data,
    setData,
  ] =
    useState([]);


  const [
    geo,
    setGeo,
  ] =
    useState(null);


  const [
    descriptions,
    setDescriptions,
  ] =
    useState({});


  const [
    modalData,
    setModalData,
  ] =
    useState(null);


  const [
    useCaseSheetByTitle,
    setUseCaseSheetByTitle,
  ] =
    useState({});


  const [
    useCaseByUrl,
    setUseCaseByUrl,
  ] =
    useState({});


  const isBasin =
    category ===
    'River Basins';


  /* ========================================================
     AVAILABLE TYPES
  ======================================================== */

  const availableTypes =
    isBasin

      ? [
          'primary',
          'products',
          'multiscale',
          'publications',
          'tools',
          'others',
          'stakeholders',
          'projects',
          'links',
          'usecase',
        ]

      : [
          'primary',
          'products',
          'stakeholders',
          'projects',
          'links',
          'usecase',
        ];


  const [
    selectedTypes,
    setSelectedTypes,
  ] =
    useState(
      () =>
        Object.fromEntries(
          availableTypes.map(
            (k) => [
              k,
              true,
            ]
          )
        )
    );


  useEffect(
    () => {

      setSelectedTypes(
        Object.fromEntries(
          availableTypes.map(
            (k) => [
              k,
              true,
            ]
          )
        )
      );

    },
    [category]
  );


  /* ========================================================
     MOBILE
  ======================================================== */

  const [
    isMobile,
    setIsMobile,
  ] =
    useState(false);


  useEffect(() => {

    const check = () => {

      if (
        typeof window !==
        'undefined'
      ) {

        setIsMobile(
          window.innerWidth <=
            768
        );
      }
    };


    check();


    window.addEventListener(
      'resize',
      check
    );


    return () =>
      window.removeEventListener(
        'resize',
        check
      );

  }, []);


  /* ========================================================
     INFO TOGGLE
  ======================================================== */

  const [
    showInfo,
    setShowInfo,
  ] =
    useState(false);


  useEffect(
    () => {

      setShowInfo(
        !isMobile
      );

    },
    [isMobile]
  );


  /* ========================================================
     MAP RESIZE
  ======================================================== */

  useEffect(
    () => {

      if (
        mapRef.current
      ) {

        setTimeout(
          () => {

            mapRef.current
              .invalidateSize();

          },
          200
        );
      }

    },
    [isMobile]
  );


  /* ========================================================
     GET FEATURE NAME
  ======================================================== */

  const getFeatureName = (f) =>

    isBasin

      ? (
          f.properties?.[
            'River basin'
          ] ||
          f.properties?.Name ||
          f.properties?.name
        )

      : (
          f.properties
            ?.adm0_name
            ?.trim() ||
          f.properties?.name ||
          f.properties?.name
        );


  /* ========================================================
     LOAD DATA
  ======================================================== */

  useEffect(() => {

    const load = async () => {

      const res =
        await fetch(
          '/assets/WA_Dashboard_Portforlio.xlsx'
        );


      const buf =
        await res.arrayBuffer();


      const wb =
        XLSX.read(
          buf,
          {
            type: 'array',
          }
        );


      const sheet =
        isBasin
          ? 'River_basins'
          : 'Countries';


      const geoUrl =
        isBasin
          ? '/assets/basin.geojson'
          : '/assets/countries1.geojson';


      const rows =
        XLSX.utils
          .sheet_to_json(
            wb.Sheets[sheet]
          );


      /* ====================================================
         DATASET METADATA
      ==================================================== */

      const descSheet =
        wb.Sheets[
          'Data descriptions'
        ];


      const descRows =
        descSheet

          ? XLSX.utils
              .sheet_to_json(
                descSheet
              )

          : [];


      const desc = {};


      descRows.forEach((r) => {

        const key =
          (
            r.Dataset ||
            ''
          )
            .toLowerCase()
            .trim();


        if (key) {

          desc[key] = {

            title:
              r.Dataset,

            description:
              r.Description,

            source:
              r.Source,

            temporal:
              r[
                'Temporal Resolution'
              ],

            spatial:
              r[
                'Spatial Resolution'
              ],
          };
        }
      });


      /* ====================================================
         USE CASE SHEET
      ==================================================== */

      const useCasesWs =
        getSheetByAny(
          wb,
          [
            'UseCases',
            'Use Cases',
            'Use Case',
            'Usecases',
            'Use_Cases',
            'Use-Case',
            'Use case(s)',
          ]
        );


      if (useCasesWs) {

        const useCaseRows =
          XLSX.utils
            .sheet_to_json(
              useCasesWs
            );


        const {
          sheetByTitle,
          byUrl,
        } =
          buildUseCaseSheetIndexDynamic(
            useCaseRows
          );


        setUseCaseSheetByTitle(
          sheetByTitle
        );


        setUseCaseByUrl(
          byUrl
        );

      } else {

        setUseCaseSheetByTitle(
          {}
        );


        setUseCaseByUrl(
          {}
        );
      }


      setData(
        rows
      );


      setDescriptions(
        desc
      );


      const geoRes =
        await fetch(
          geoUrl
        );


      const geoJson =
        await geoRes.json();


      setGeo(
        geoJson
      );


      setExpandedRegion(
        null
      );
    };


    load();

  }, [category]);


  /* ========================================================
     INITIALIZE MAP
  ======================================================== */

  useEffect(() => {

    if (
      !mapRef.current
    ) {

      const map =
        L.map(
          'map',
          {
            center: [
              0,
              25,
            ],

            zoom: 3.5,

            zoomControl: true,
          }
        );


      /* ====================================================
         LIGHT BASEMAP — NO API KEY / NO CARTO WATERMARK
         Esri World Light Gray gives the clean, light look
         while avoiding the visible tile grid from the
         previous basemap. A separate reference layer adds
         subtle country/place labels.
      ==================================================== */

      L.tileLayer(
        'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}',
        {
          maxZoom: 16,
          attribution:
            'Tiles &copy; Esri — Esri, DeLorme, NAVTEQ, USGS, Intermap, iPC, NRCAN, Esri Japan, METI, Esri China (Hong Kong), Esri (Thailand), TomTom, 2012',
          updateWhenIdle: false,
          keepBuffer: 4,
        }
      ).addTo(map);

      /* Subtle labels/boundaries on top of the light basemap */
      L.tileLayer(
        'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Reference/MapServer/tile/{z}/{y}/{x}',
        {
          maxZoom: 16,
          opacity: 0.72,
          attribution: 'Labels &copy; Esri',
          updateWhenIdle: false,
          keepBuffer: 4,
        }
      ).addTo(map);


      mapRef.current =
        map;
    }

  }, []);


  /* ========================================================
     DRAW MAP + MARKERS + LEGEND
  ======================================================== */

  useEffect(() => {

    const map =
      mapRef.current;


    if (
      !geo ||
      !data.length ||
      !map
    ) {

      return;
    }


    /* ======================================================
       CLEAR OLD LAYERS
    ====================================================== */

    if (
      geoJsonRef.current
    ) {

      geoJsonRef.current
        .remove();
    }


    if (
      markerGroupRef.current
    ) {

      markerGroupRef.current
        .clearLayers();


      map.removeLayer(
        markerGroupRef.current
      );
    }


    if (
      legendRef.current
    ) {

      map.removeControl(
        legendRef.current
      );
    }


    /* ======================================================
       HIGHLIGHT COLORS
    ====================================================== */

    const highlightColors = {

      ethiopia:
        '#ffe0b2',

      ghana:
        '#d1c4e9',

      'burkina faso':
        '#c8e6c9',

      zambia:
        '#b2ebf2',
    };


    /* ======================================================
       GEOJSON
    ====================================================== */

    geoJsonRef.current =
      L.geoJSON(
        geo,
        {

          style:
            (feature) => {

              const name =
                getFeatureName(
                  feature
                )
                  ?.trim()
                  .toLowerCase();


              const fillColor =

                highlightColors[
                  name
                ] ||

                (
                  norm(
                    expandedRegion
                  ) === name

                    ? '#a3e9a4'

                    : '#eafbea'
                );


              return {

                color:
                  '#aaa',

                weight:
                  1.5,

                fillColor,

                fillOpacity:
                  0.6,
              };
            },


          onEachFeature:
            (
              feature,
              layer
            ) => {

              const name =
                getFeatureName(
                  feature
                );


              layer.on(
                'click',
                () =>
                  setExpandedRegion(
                    (prev) =>
                      prev === name
                        ? null
                        : name
                  )
              );


              layer.bindTooltip(
                name,
                {
                  sticky: true,
                }
              );
            },
        }
      )
        .addTo(map);


    /* ======================================================
       KEY FIELD
    ====================================================== */

    const keyField =
      isBasin
        ? 'River basin'
        : 'Country';


    const layerGroup =
      L.layerGroup();


    /* ======================================================
       DATA ROWS
    ====================================================== */

    data.forEach((row) => {

      const name =
        getCell(
          row,
          keyField
        );


      if (
        !name ||
        (
          selectedCountry !==
            'All' &&
          name !==
            selectedCountry
        )
      ) {

        return;
      }


      const feature =
        geo.features.find(
          (f) =>
            norm(
              getFeatureName(f)
            ) ===
            norm(name)
        );


      if (!feature) {

        return;
      }


      const bounds =
        L.geoJSON(
          feature
        )
          .getBounds();


      if (
        !bounds.isValid()
      ) {

        return;
      }


      const {
        lat,
        lng,
      } =
        bounds.getCenter();


      /* ====================================================
         MAIN LOCATION MARKER
      ==================================================== */

      const marker =
        L.marker(
          [
            lat,
            lng,
          ],
          {
            icon:
              locationIcon,

            title:
              name,
          }
        )
          .addTo(
            layerGroup
          );


      /* ====================================================
         COUNT AVAILABLE TYPES
      ==================================================== */

      const count =
        Object.keys(
          selectedTypes
        )
          .filter(
            (k) => {

              const field =
                getFieldLabel(k);


              const val =
                getCell(
                  row,
                  field
                );


              return (
                val &&
                String(val)
                  .trim() !== ''
              );
            }
          )
          .length;


      marker.bindTooltip(
        `
          <strong
            style="
              font-size:18px;
              font-weight:bold;
            "
          >
            ${count} dataset types available
          </strong>
        `,
        {
          permanent:
            false,

          direction:
            'top',

          opacity:
            1,

          className:
            'marker-tooltip',
        }
      );


      marker.on(
        'click',
        () =>
          setExpandedRegion(
            name
          )
      );


      /* ====================================================
         EXPANDED REGION
      ==================================================== */

      if (
        norm(
          expandedRegion
        ) ===
        norm(name)
      ) {

        const centerPoint =
          map.latLngToLayerPoint(
            [
              lat,
              lng,
            ]
          );


        const visible =
          Object.keys(
            selectedTypes
          )
            .filter(
              (k) =>
                selectedTypes[k]
            );


        const angleStep =
          (
            2 *
            Math.PI
          ) /
          visible.length;


        const turfPolygon =
          turf.feature(
            feature.geometry
          );


        let radius =
          100;


        visible.forEach(
          (
            key,
            index
          ) => {

            /*
              INTERNAL EXCEL FIELD
            */

            const field =
              getFieldLabel(
                key
              );


            const value =
              getCell(
                row,
                field
              );


            if (!value) {

              return;
            }


            const items =
              String(value)

                .split(
                  /\r?\n|[;,]|•/g
                )

                .map(
                  (i) =>
                    i.trim()
                )

                .filter(
                  Boolean
                );


            if (
              !items.length
            ) {

              return;
            }


            const angle =
              index *
              angleStep;


            for (
              let attempt = 0;
              attempt < 10;
              attempt++
            ) {

              const offsetPoint =
                L.point(

                  centerPoint.x +
                    radius *
                      Math.cos(
                        angle
                      ),

                  centerPoint.y +
                    radius *
                      Math.sin(
                        angle
                      )
                );


              const offsetLatLng =
                map.layerPointToLatLng(
                  offsetPoint
                );


              const pt =
                turf.point(
                  [
                    offsetLatLng.lng,
                    offsetLatLng.lat,
                  ]
                );


              if (
                turf.booleanPointInPolygon(
                  pt,
                  turfPolygon
                )
              ) {

                /* ==========================================
                   CONNECTION LINE
                ========================================== */

                L.polyline(
                  [
                    [
                      lat,
                      lng,
                    ],

                    offsetLatLng,
                  ],
                  {
                    color:
                      '#336699',

                    weight:
                      1.5,
                  }
                )
                  .addTo(
                    layerGroup
                  );


                /* ==========================================
                   ICON
                ========================================== */

                const iconHtml =

                  key ===
                  'products'

                    ? `
                      <img
                        src="${imageIcons.products}"
                        style="
                          width:32px;
                          height:32px;
                        "
                      />
                    `

                    : ReactDOMServer
                        .renderToString(
                          getIcon(
                            key
                          )
                        );


                /*
                  DISPLAY LABEL

                  tools -> Dashboard
                */

                const tooltipLabel =
                  getDisplayLabel(
                    key
                  );


                /* ==========================================
                   DATASET MARKER
                ========================================== */

                const datasetMarker =
                  L.marker(
                    offsetLatLng,
                    {

                      icon:
                        L.divIcon(
                          {

                            className:
                              '',

                            iconSize:
                              [
                                70,
                                70,
                              ],

                            html: `
                              <div
                                style="
                                  width:64px;
                                  height:64px;
                                  font-size:20px;
                                  background:
                                    linear-gradient(
                                      135deg,
                                      #fff,
                                      #e6f7ff
                                    );
                                  border:
                                    2px solid
                                    #0066cc;
                                  border-radius:
                                    50%;
                                  display:flex;
                                  align-items:center;
                                  justify-content:center;
                                  box-shadow:
                                    0 0 8px
                                    rgba(
                                      0,
                                      0,
                                      0,
                                      .4
                                    );
                                  position:relative;
                                  cursor:pointer;
                                "
                              >

                                ${iconHtml}

                                <div
                                  class="tooltip"
                                  style="
                                    display:none;
                                    position:absolute;
                                    top:72px;
                                    left:50%;
                                    transform:
                                      translateX(
                                        -50%
                                      );
                                    background:#fff;
                                    color:#E86933;
                                    font-weight:bold;
                                    font-size:24px;
                                    border:
                                      2px solid
                                      #003366;
                                    padding:
                                      6px 10px;
                                    border-radius:
                                      8px;
                                    white-space:
                                      nowrap;
                                    box-shadow:
                                      0 4px 12px
                                      rgba(
                                        0,
                                        0,
                                        0,
                                        .2
                                      );
                                  "
                                >

                                  ${tooltipLabel}

                                </div>

                              </div>
                            `,
                          }
                        ),
                    }
                  );


                /* ==========================================
                   MARKER CLICK
                ========================================== */

                datasetMarker.on(
                  'click',
                  () => {

                    /* ======================================
                       DATASETS
                    ====================================== */

                    if (
                      [
                        'Primary Data Sets',
                        'Water Data Products',
                      ]
                        .includes(
                          field
                        )
                    ) {

                      const metaItems =
                        items.map(
                          (ds) => {

                            const meta =
                              descriptions[
                                String(ds)
                                  .toLowerCase()
                              ] ||
                              {};


                            return {

                              dataset:
                                ds,

                              title:
                                meta.title ||
                                '',

                              description:
                                meta.description ||
                                '',

                              source:
                                meta.source ||
                                '',

                              temporal:
                                meta.temporal ||
                                '',

                              spatial:
                                meta.spatial ||
                                '',
                            };
                          }
                        );


                      setModalData(
                        {
                          name,

                          title:
                            field,

                          mode:
                            'datasets',

                          items:
                            metaItems,
                        }
                      );
                    }

                    /* ======================================
                       LINKS
                    ====================================== */

                    else if (
                      field ===
                        'Links' ||
                      field ===
                        'Link'
                    ) {

                      const linkItems =
                        parseCell(
                          value
                        )
                          .map(
                            (x) => ({
                              title:
                                x.title ||
                                x.url ||
                                'Link',

                              url:
                                x.url ||
                                (
                                  isUrl(
                                    x.title
                                  )
                                    ? x.title
                                    : ''
                                ),

                              description:
                                x.description ||
                                '',
                            })
                          );


                      setModalData(
                        {
                          name,

                          title:
                            'Links',

                          mode:
                            'links',

                          items:
                            linkItems,
                        }
                      );
                    }

                    /* ======================================
                       STAKEHOLDERS
                    ====================================== */

                    else if (
                      field ===
                      'Stakeholders'
                    ) {

                      const stakeholders =
                        parseStakeholders(
                          value
                        );


                      setModalData(
                        {
                          name,

                          title:
                            '',

                          mode:
                            'stakeholders',

                          items:
                            stakeholders,
                        }
                      );
                    }

                    /* ======================================
                       USE CASE
                    ====================================== */

                    else if (
                      [
                        'UseCase',
                        'Use Case',
                        'Use Cases',
                      ]
                        .includes(
                          field
                        )
                    ) {

                      const ucItems =
                        parseCell(
                          value
                        )
                          .map(
                            (x) => {

                              const incomingTitle =
                                (
                                  x.title ||
                                  'Use case'
                                )
                                  .trim();


                              const region =
                                name;


                              let extra =
                                pickUseCaseFromSheet(
                                  useCaseSheetByTitle,
                                  incomingTitle,
                                  region
                                );


                              if (
                                (
                                  !extra ||
                                  !extra.title
                                ) &&
                                x.url &&
                                useCaseByUrl[
                                  x.url
                                ]
                              ) {

                                extra =
                                  useCaseByUrl[
                                    x.url
                                  ];
                              }


                              const descFromCell =
                                emptyish(
                                  x.description
                                );


                              const desc =
                                descFromCell ||
                                extra.description ||
                                '';


                              const url =
                                x.url ||
                                extra.url ||
                                '';


                              return {

                                title:
                                  extra.title ||
                                  incomingTitle,

                                url,

                                description:
                                  desc,

                                activities:
                                  extra.activities ||
                                  [],

                                problem:
                                  extra.problem ||
                                  '',

                                solutions:
                                  extra.solutions ||
                                  '',

                                delivery:
                                  extra.delivery ||
                                  '',

                                owner:
                                  extra.owner ||
                                  '',

                                beneficiaries:
                                  extra.beneficiaries ||
                                  '',

                                remark:
                                  extra.remark ||
                                  '',

                                isVideo:
                                  url &&
                                  (
                                    isYouTube(
                                      url
                                    ) ||
                                    isVimeo(
                                      url
                                    ) ||
                                    isMP4(
                                      url
                                    )
                                  ),
                              };
                            }
                          );


                      setModalData(
                        {
                          name,

                          title:
                            'UseCase',

                          mode:
                            'usecase',

                          items:
                            ucItems,
                        }
                      );
                    }

                    /* ======================================
                       GENERIC
                    ====================================== */

                    else {

                      const generic =
                        parseCell(
                          value
                        )
                          .map(
                            (x) => ({
                              title:
                                x.title ||
                                x.url ||
                                'Item',

                              url:
                                x.url,

                              description:
                                x.description ||
                                '',
                            })
                          );


                      setModalData(
                        {
                          name,

                          /*
                            USER DISPLAY LABEL

                            Tools -> Dashboard
                          */

                          title:
                            getDisplayLabel(
                              key
                            ),

                          mode:
                            'generic',

                          items:
                            generic,
                        }
                      );
                    }
                  }
                );


                datasetMarker
                  .addTo(
                    layerGroup
                  );


                break;
              }


              radius -= 10;
            }
          }
        );
      }
    });


    layerGroup
      .addTo(map);


    markerGroupRef.current =
      layerGroup;


    /* ======================================================
       LEGEND
    ====================================================== */

    const legend =
      L.control(
        {
          position:
            'bottomright',
        }
      );


    legend.onAdd =
      function () {

        const div =
          L.DomUtil.create(
            'div',
            'info legend'
          );


        Object.assign(
          div.style,
          {

            background:
              '#ffffff',

            padding:
              isMobile
                ? '8px 10px'
                : '14px 18px',

            borderRadius:
              '10px',

            boxShadow:
              '0 2px 12px rgba(0,0,0,0.2)',

            fontSize:
              isMobile
                ? '12px'
                : '16px',

            fontWeight:
              'bold',

            color:
              '#222',

            maxWidth:
              isMobile
                ? '70vw'
                : '260px',

            maxHeight:
              isMobile
                ? '40vh'
                : 'none',

            overflowY:
              isMobile
                ? 'auto'
                : 'visible',

            lineHeight:
              '1.7',

            fontFamily:
              'Segoe UI, sans-serif',
          }
        );


        div.innerHTML = `
          <h4
            style="
              margin:
                0 0 8px 0;
              font-size:
                ${
                  isMobile
                    ? '14px'
                    : '18px'
                };
              color:
                #00366e;
              font-weight:
                bold;
            "
          >
            Legend
          </h4>
        `;


        Object.keys(
          selectedTypes
        )
          .forEach(
            (key) => {

              if (
                !selectedTypes[
                  key
                ]
              ) {

                return;
              }


              /*
                Same getIcon() used here,
                therefore Dashboard uses
                MdSpaceDashboard in legend too.
              */

              const iconHTML =

                key ===
                'products'

                  ? `
                    <img
                      src="${imageIcons.products}"
                      style="
                        width:22px;
                        height:22px;
                      "
                    />
                  `

                  : ReactDOMServer
                      .renderToString(
                        getIcon(
                          key
                        )
                      );


              /*
                Display Dashboard instead of Tools
              */

              const legendLabel =
                getDisplayLabel(
                  key
                );


              div.innerHTML += `

                <div
                  style="
                    display:flex;
                    align-items:center;
                    margin-bottom:6px;
                  "
                >

                  <div
                    style="
                      width:24px;
                      height:24px;
                      display:flex;
                      align-items:center;
                      justify-content:center;
                      margin-right:10px;
                    "
                  >

                    ${iconHTML}

                  </div>


                  <span
                    style="
                      font-weight:bold;
                    "
                  >

                    ${legendLabel}

                  </span>

                </div>
              `;
            }
          );


        return div;
      };


    legend
      .addTo(map);


    legendRef.current =
      legend;

  }, [

    data,

    geo,

    expandedRegion,

    selectedTypes,

    category,

    selectedCountry,

    descriptions,

    useCaseSheetByTitle,

    useCaseByUrl,

    isMobile,
  ]);


  /* ========================================================
     RENDER
  ======================================================== */

  return (

    <div

      style={{

        display:
          'flex',

        flexDirection:
          isMobile
            ? 'column'
            : 'row',

        height:
          isMobile
            ? 'auto'
            : '100vh',

        minHeight:
          '100vh',

        position:
          'relative',
      }}
    >

      {/* ====================================================
          SIDEBAR
      ==================================================== */}

      <Sidebar1

        selectedCategory={
          category
        }

        onCategoryChange={
          setCategory
        }

        selectedDataSets={
          selectedTypes
        }

        onDataSetChange={
          setSelectedTypes
        }

        selectedCountry={
          selectedCountry
        }

        onCountryChange={
          setSelectedCountry
        }

        csvData={
          data
        }
      />


      {/* ====================================================
          MAP
      ==================================================== */}

      <div

        id="map"

        style={{

          flex:
            1,

          minHeight:
            isMobile
              ? '60vh'
              : 'auto',
        }}
      />


      {/* ====================================================
          INFO BUTTON
      ==================================================== */}

      <div

        style={{

          position:
            'absolute',

          top:
            10,

          right:
            isMobile
              ? 10
              : 'auto',

          left:
            isMobile
              ? 'auto'
              : 650,

          zIndex:
            9999,
        }}
      >

        <div

          style={{

            position:
              'relative',

            display:
              'inline-block',
          }}
        >

          <div

            onClick={
              () =>
                setShowInfo(
                  (prev) =>
                    !prev
                )
            }

            style={{

              width:
                isMobile
                  ? 28
                  : 32,

              height:
                isMobile
                  ? 28
                  : 32,

              borderRadius:
                '50%',

              backgroundColor:
                '#003366',

              color:
                '#fff',

              fontWeight:
                'bold',

              fontSize:
                isMobile
                  ? 14
                  : 18,

              display:
                'flex',

              alignItems:
                'center',

              justifyContent:
                'center',

              boxShadow:
                '0 2px 6px rgba(0,0,0,0.3)',

              cursor:
                'pointer',
            }}

            title="Information"
          >

            i

          </div>


          {/* =================================================
              INFO PANEL
          ================================================= */}

          {showInfo && (

            <div

              style={{

                position:
                  'absolute',

                top:
                  isMobile
                    ? 36
                    : 40,

                right:
                  0,

                background:
                  '#fff',

                padding:
                  '12px 16px',

                borderRadius:
                  10,

                boxShadow:
                  '0 4px 12px rgba(0,0,0,0.2)',

                width:
                  isMobile
                    ? '80vw'
                    : 260,

                maxWidth:
                  isMobile
                    ? '90vw'
                    : '260px',

                fontSize:
                  isMobile
                    ? 12
                    : 14,

                color:
                  '#333',
              }}
            >

              <strong>
                How to Use the Map:
              </strong>


              <ul

                style={{

                  margin:
                    '10px 0 0',

                  paddingLeft:
                    18,

                  fontSize:
                    isMobile
                      ? 12
                      : 14,

                  lineHeight:
                    1.5,
                }}
              >

                <li>
                  📍 Click a marker to reveal dataset types
                </li>

                <li>
                  ⭕ Click a circle to open details
                </li>

                <li>
                  🔗 UseCase loads dynamically from the{' '}
                  <em>
                    UseCases
                  </em>{' '}
                  sheet
                </li>

              </ul>

            </div>
          )}

        </div>

      </div>


      {/* ====================================================
          MODAL
      ==================================================== */}

      {modalData && (

        <div

          style={{

            position:
              'fixed',

            inset:
              0,

            background:
              'rgba(0,0,0,0.4)',

            display:
              'flex',

            alignItems:
              'center',

            justifyContent:
              'center',

            zIndex:
              9999,
          }}
        >

          <div

            style={{

              background:
                'white',

              padding:
                isMobile
                  ? 16
                  : 32,

              maxWidth:
                '90vw',

              maxHeight:
                isMobile
                  ? '80vh'
                  : '85vh',

              overflow:
                'auto',

              borderRadius:
                16,

              fontSize:
                isMobile
                  ? 14
                  : 18,

              width:
                isMobile
                  ? '96vw'
                  : 'min(1100px, 90vw)',
            }}
          >

            {/* =================================================
                MODAL TITLE
            ================================================= */}

            <h2

              style={{

                marginBottom:
                  16,

                color:
                  '#003366',

                fontSize:
                  isMobile
                    ? 18
                    : 24,
              }}
            >

              {
                modalData.mode ===
                'stakeholders'

                  ? modalData.name

                  : `${modalData.name} — ${modalData.title}`
              }

            </h2>


            {/* =================================================
                DATASETS
            ================================================= */}

            {modalData.mode ===
              'datasets' && (

              <div

                style={{

                  width:
                    '100%',

                  overflowX:
                    'auto',
                }}
              >

                <table

                  style={{

                    width:
                      '100%',

                    minWidth:
                      isMobile
                        ? 600
                        : '100%',

                    borderCollapse:
                      'collapse',
                  }}
                >

                  <thead

                    style={{

                      backgroundColor:
                        '#003366',

                      color:
                        'white',
                    }}
                  >

                    <tr>

                      <th
                        style={{
                          padding:
                            isMobile
                              ? 8
                              : 14,

                          fontSize:
                            isMobile
                              ? 12
                              : 14,
                        }}
                      >
                        Dataset
                      </th>

                      <th
                        style={{
                          padding:
                            isMobile
                              ? 8
                              : 14,

                          fontSize:
                            isMobile
                              ? 12
                              : 14,
                        }}
                      >
                        Full Name
                      </th>

                      <th
                        style={{
                          padding:
                            isMobile
                              ? 8
                              : 14,

                          fontSize:
                            isMobile
                              ? 12
                              : 14,
                        }}
                      >
                        Temporal
                      </th>

                      <th
                        style={{
                          padding:
                            isMobile
                              ? 8
                              : 14,

                          fontSize:
                            isMobile
                              ? 12
                              : 14,
                        }}
                      >
                        Spatial
                      </th>

                      <th
                        style={{
                          padding:
                            isMobile
                              ? 8
                              : 14,

                          fontSize:
                            isMobile
                              ? 12
                              : 14,
                        }}
                      >
                        Description
                      </th>

                      <th
                        style={{
                          padding:
                            isMobile
                              ? 8
                              : 14,

                          fontSize:
                            isMobile
                              ? 12
                              : 14,
                        }}
                      >
                        Source
                      </th>

                    </tr>

                  </thead>


                  <tbody>

                    {modalData.items.map(
                      (
                        row,
                        i
                      ) => (

                        <tr

                          key={i}

                          style={{

                            backgroundColor:
                              i % 2 === 0
                                ? '#f9f9f9'
                                : '#ffffff',
                          }}
                        >

                          <td

                            style={{

                              padding:
                                isMobile
                                  ? 8
                                  : 14,

                              fontWeight:
                                'bold',

                              fontSize:
                                isMobile
                                  ? 12
                                  : 14,
                            }}
                          >

                            {row.dataset}

                          </td>


                          <td
                            style={{
                              padding:
                                isMobile
                                  ? 8
                                  : 14,

                              fontSize:
                                isMobile
                                  ? 12
                                  : 14,
                            }}
                          >
                            {row.title}
                          </td>


                          <td
                            style={{
                              padding:
                                isMobile
                                  ? 8
                                  : 14,

                              fontSize:
                                isMobile
                                  ? 12
                                  : 14,
                            }}
                          >
                            {row.temporal}
                          </td>


                          <td
                            style={{
                              padding:
                                isMobile
                                  ? 8
                                  : 14,

                              fontSize:
                                isMobile
                                  ? 12
                                  : 14,
                            }}
                          >
                            {row.spatial}
                          </td>


                          <td
                            style={{
                              padding:
                                isMobile
                                  ? 8
                                  : 14,

                              fontSize:
                                isMobile
                                  ? 12
                                  : 14,
                            }}
                          >
                            {row.description}
                          </td>


                          <td

                            style={{

                              padding:
                                isMobile
                                  ? 8
                                  : 14,

                              fontSize:
                                isMobile
                                  ? 12
                                  : 14,
                            }}
                          >

                            {row.source ? (

                              <a

                                href={
                                  row.source
                                }

                                target="_blank"

                                rel="noopener noreferrer"

                                style={{

                                  color:
                                    '#0077cc',
                                }}
                              >

                                link

                              </a>

                            ) : (

                              ''

                            )}

                          </td>

                        </tr>

                      )
                    )}

                  </tbody>

                </table>

              </div>
            )}


            {/* =================================================
                LINKS
            ================================================= */}

            {modalData.mode ===
              'links' && (

              <div

                style={{

                  display:
                    'grid',

                  gap:
                    16,
                }}
              >

                {modalData.items.map(
                  (
                    x,
                    i
                  ) => (

                    <div

                      key={i}

                      style={{

                        padding:
                          16,

                        border:
                          '1px solid #e5e7eb',

                        borderRadius:
                          10,
                      }}
                    >

                      <a

                        href={
                          x.url
                        }

                        target="_blank"

                        rel="noopener noreferrer"

                        style={{

                          fontWeight:
                            'bold',

                          color:
                            '#0b67c2',

                          textDecoration:
                            'underline',
                        }}
                      >

                        {x.title}

                      </a>


                      {x.description && (

                        <p

                          style={{

                            marginTop:
                              8,

                            whiteSpace:
                              'pre-line',
                          }}
                        >

                          {x.description}

                        </p>
                      )}

                    </div>

                  )
                )}

              </div>
            )}


            {/* =================================================
                STAKEHOLDERS
            ================================================= */}

            {modalData.mode ===
              'stakeholders' && (

              <div

                style={{

                  display:
                    'grid',

                  gap:
                    16,
                }}
              >

                {modalData.items.map(
                  (
                    s,
                    idx
                  ) => {

                    const rawSource =
                      (
                        Array.isArray(
                          s.lines
                        ) &&
                        s.lines.length

                          ? s.lines.join(
                              '\n'
                            )

                          : s.body ||
                            ''
                      )

                        .replace(
                          /\r/g,
                          ''
                        )

                        .trim();


                    let parts =
                      rawSource

                        .split(
                          /(?=\(?\d{1,2}\)?[.)]\s)|[;\n]+/g
                        )

                        .map(
                          (t) =>
                            t.trim()
                        )

                        .filter(
                          Boolean
                        );


                    const cleanedParts =
                      parts

                        .map(
                          (t) =>
                            t.replace(
                              /^\(?\d{1,2}\)?[.)]\s*/,
                              ''
                            )
                        )

                        .map(
                          (t) =>
                            t.replace(
                              /^[•*·\-–—]+\s*/,
                              ''
                            )
                        )

                        .map(
                          (t) =>
                            t.replace(
                              /\s{2,}/g,
                              ' '
                            )
                        )

                        .map(
                          (t) =>
                            t.replace(
                              /\s*[;•]\s*$/g,
                              ''
                            )
                        )

                        .filter(
                          Boolean
                        );


                    const desc =
                      cleanedParts.join(
                        '\n'
                      );


                    const hasDesc =
                      !!desc;


                    return (

                      <div

                        key={
                          idx
                        }

                        style={{

                          padding:
                            16,

                          border:
                            '1px solid #e5e7eb',

                          borderRadius:
                            12,
                        }}
                      >

                        <div

                          style={{

                            fontWeight:
                              'bold',

                            fontSize:
                              18,

                            color:
                              '#0a3a72',

                            marginBottom:
                              hasDesc
                                ? 8
                                : 0,
                          }}
                        >

                          {
                            hasDesc

                              ? (
                                  s.name ||
                                  'Stakeholder'
                                )

                              : `${idx + 1}) ${
                                  s.name ||
                                  'Stakeholder'
                                }`
                          }


                          {s.url && (

                            <>

                              {' '}

                              —{' '}

                              <a

                                href={
                                  s.url
                                }

                                target="_blank"

                                rel="noopener noreferrer"

                                style={{

                                  color:
                                    '#0b67c2',

                                  textDecoration:
                                    'underline',
                                }}
                              >

                                website

                              </a>

                            </>
                          )}

                        </div>


                        {hasDesc && (

                          <div

                            style={{

                              whiteSpace:
                                'pre-line',

                              lineHeight:
                                1.6,
                            }}
                          >

                            {desc}

                          </div>
                        )}

                      </div>
                    );
                  }
                )}

              </div>
            )}


            {/* =================================================
                USE CASE
            ================================================= */}

            {modalData.mode ===
              'usecase' && (

              <div

                style={{

                  display:
                    'grid',

                  gap:
                    20,
                }}
              >

                {modalData.items.map(
                  (
                    x,
                    i
                  ) => {

                    const {
                      preamble,
                      list,
                    } =
                      peelPreambleAndList(
                        x.description
                      );


                    return (

                      <div

                        key={i}

                        style={{

                          padding:
                            16,

                          border:
                            '1px solid #e5e7eb',

                          borderRadius:
                            12,
                        }}
                      >

                        <div

                          style={{

                            display:
                              'flex',

                            flexDirection:
                              'column',

                            gap:
                              10,
                          }}
                        >

                          {/* =====================================
                              TITLE
                          ===================================== */}

                          <div

                            style={{

                              fontWeight:
                                'bold',

                              fontSize:
                                20,

                              color:
                                '#002b5c',
                            }}
                          >

                            {x.title}

                          </div>


                          {/* =====================================
                              DESCRIPTION
                          ===================================== */}

                          {(
                            preamble ||
                            list.length > 0
                          ) && (

                            <div>

                              <strong>
                                Description:
                              </strong>{' '}


                              {preamble && (

                                <span

                                  style={{

                                    whiteSpace:
                                      'pre-line',
                                  }}
                                >

                                  {preamble}

                                </span>
                              )}


                              {list.length >
                                0 && (

                                <ul

                                  style={{

                                    margin:
                                      '6px 0 0 18px',
                                  }}
                                >

                                  {list.map(
                                    (
                                      li,
                                      idx2
                                    ) => (

                                      <li

                                        key={
                                          idx2
                                        }

                                        style={{

                                          whiteSpace:
                                            'pre-line',
                                        }}
                                      >

                                        {li}

                                      </li>

                                    )
                                  )}

                                </ul>
                              )}

                            </div>
                          )}


                          {/* =====================================
                              ACTIVITIES
                          ===================================== */}

                          {
                            Array.isArray(
                              x.activities
                            ) &&
                            x.activities.length >
                              0 && (

                              <div>

                                <strong>
                                  Activities:
                                </strong>


                                <ul

                                  style={{

                                    margin:
                                      '6px 0 0 18px',
                                  }}
                                >

                                  {
                                    x.activities.map(
                                      (
                                        a,
                                        idx2
                                      ) => (

                                        <li

                                          key={
                                            idx2
                                          }

                                          style={{

                                            whiteSpace:
                                              'pre-line',
                                          }}
                                        >

                                          {a}

                                        </li>
                                      )
                                    )
                                  }

                                </ul>

                              </div>
                            )
                          }


                          {/* =====================================
                              PROBLEM
                          ===================================== */}

                          {x.problem &&
                            (() => {

                              const p =
                                peelPreambleAndList(
                                  x.problem
                                );


                              return (

                                <div>

                                  <strong>
                                    Problem:
                                  </strong>{' '}


                                  {p.preamble && (

                                    <span

                                      style={{

                                        whiteSpace:
                                          'pre-line',
                                      }}
                                    >

                                      {p.preamble}

                                    </span>
                                  )}


                                  {p.list.length >
                                    0 && (

                                    <ul

                                      style={{

                                        margin:
                                          '6px 0 0 18px',
                                      }}
                                    >

                                      {
                                        p.list.map(
                                          (
                                            li,
                                            k
                                          ) => (

                                            <li

                                              key={
                                                k
                                              }

                                              style={{

                                                whiteSpace:
                                                  'pre-line',
                                              }}
                                            >

                                              {li}

                                            </li>

                                          )
                                        )
                                      }

                                    </ul>
                                  )}

                                </div>
                              );
                            })()
                          }


                          {/* =====================================
                              SOLUTIONS
                          ===================================== */}

                          {x.solutions &&
                            (() => {

                              const s =
                                peelPreambleAndList(
                                  x.solutions
                                );


                              return (

                                <div>

                                  <strong>
                                    Solutions:
                                  </strong>{' '}


                                  {s.preamble && (

                                    <span

                                      style={{

                                        whiteSpace:
                                          'pre-line',
                                      }}
                                    >

                                      {s.preamble}

                                    </span>
                                  )}


                                  {s.list.length >
                                    0 && (

                                    <ul

                                      style={{

                                        margin:
                                          '6px 0 0 18px',
                                      }}
                                    >

                                      {
                                        s.list.map(
                                          (
                                            li,
                                            k
                                          ) => (

                                            <li

                                              key={
                                                k
                                              }

                                              style={{

                                                whiteSpace:
                                                  'pre-line',
                                              }}
                                            >

                                              {li}

                                            </li>

                                          )
                                        )
                                      }

                                    </ul>
                                  )}

                                </div>
                              );
                            })()
                          }


                          {/* =====================================
                              DELIVERY
                          ===================================== */}

                          {x.delivery &&
                            (() => {

                              const d =
                                peelPreambleAndList(
                                  x.delivery
                                );


                              return (

                                <div>

                                  <strong>
                                    Delivery mechanisms:
                                  </strong>{' '}


                                  {d.preamble && (

                                    <span

                                      style={{

                                        whiteSpace:
                                          'pre-line',
                                      }}
                                    >

                                      {d.preamble}

                                    </span>
                                  )}


                                  {d.list.length >
                                    0 && (

                                    <ul

                                      style={{

                                        margin:
                                          '6px 0 0 18px',
                                      }}
                                    >

                                      {
                                        d.list.map(
                                          (
                                            li,
                                            k
                                          ) => (

                                            <li

                                              key={
                                                k
                                              }

                                              style={{

                                                whiteSpace:
                                                  'pre-line',
                                              }}
                                            >

                                              {li}

                                            </li>

                                          )
                                        )
                                      }

                                    </ul>
                                  )}

                                </div>
                              );
                            })()
                          }


                          {/* =====================================
                              OWNER
                          ===================================== */}

                          {x.owner && (

                            <div

                              style={{

                                whiteSpace:
                                  'pre-line',
                              }}
                            >

                              <strong>
                                Owner:
                              </strong>{' '}

                              {x.owner}

                            </div>
                          )}


                          {/* =====================================
                              BENEFICIARIES
                          ===================================== */}

                          {x.beneficiaries && (

                            <div

                              style={{

                                whiteSpace:
                                  'pre-line',
                              }}
                            >

                              <strong>
                                Beneficiaries:
                              </strong>{' '}

                              {x.beneficiaries}

                            </div>
                          )}


                          {/* =====================================
                              REMARK
                          ===================================== */}

                          {x.remark && (

                            <div

                              style={{

                                whiteSpace:
                                  'pre-line',
                              }}
                            >

                              <strong>
                                Remark:
                              </strong>{' '}

                              {x.remark}

                            </div>
                          )}


                          {/* =====================================
                              VIDEO / URL
                          ===================================== */}

                          {x.isVideo ? (

                            <Video

                              url={
                                x.url
                              }

                              title={
                                x.title
                              }
                            />

                          ) : x.url ? (

                            <a

                              href={
                                x.url
                              }

                              target="_blank"

                              rel="noopener noreferrer"

                              style={{

                                color:
                                  '#0b67c2',

                                textDecoration:
                                  'underline',
                              }}
                            >

                              Open link

                            </a>

                          ) : null}

                        </div>

                      </div>
                    );
                  }
                )}

              </div>
            )}


            {/* =================================================
                GENERIC ITEMS
            ================================================= */}

            {modalData.mode ===
              'generic' && (

              <ul

                style={{

                  margin:
                    0,

                  paddingLeft:
                    20,
                }}
              >

                {modalData.items.map(
                  (
                    x,
                    i
                  ) => (

                    <li

                      key={i}

                      style={{

                        marginBottom:
                          12,
                      }}
                    >

                      <span

                        style={{

                          fontWeight:
                            'bold',
                        }}
                      >

                        {x.title}

                      </span>{' '}


                      {x.url && (

                        <a

                          href={
                            x.url
                          }

                          target="_blank"

                          rel="noopener noreferrer"

                          style={{

                            color:
                              '#0b67c2',
                          }}
                        >

                          (link)

                        </a>
                      )}


                      {x.description && (

                        <div

                          style={{

                            marginTop:
                              6,

                            whiteSpace:
                              'pre-line',
                          }}
                        >

                          {x.description}

                        </div>
                      )}

                    </li>

                  )
                )}

              </ul>
            )}


            {/* =================================================
                CLOSE
            ================================================= */}

            <button

              onClick={
                () =>
                  setModalData(
                    null
                  )
              }

              style={{

                marginTop:
                  20,

                backgroundColor:
                  '#003366',

                color:
                  'white',

                padding:
                  '10px 20px',

                border:
                  'none',

                borderRadius:
                  8,

                fontSize:
                  16,

                cursor:
                  'pointer',
              }}
            >

              Close

            </button>

          </div>

        </div>
      )}

    </div>
  );
};


export default MapView;